import { clamp } from '../core/math';
import { dayNumber } from '../core/time';
import type { SiteInput } from '../data/types';
import { capacity } from './capacity';
import { B, REGIONS, SOILS } from './constants';
import { earthBalance } from './earthBalance';
import { calibrate, planProgress } from './forecast';
import { KINDS, SITE_COLORS } from './kinds';
import { buildContext, buildDesign, buildGrid } from './terrain';
import type { Capacity, Plan, Site, Task } from './types';
import { buildWorkCurve, workAt } from './workCurve';

const round1 = (v: number): number => Math.round(v * 10) / 10;
const stripPrefSuffix = (pref: string): string => pref.replace(/(都|府|県)$/, '');

/**
 * 必要な日当り量 need（地山 m³/稼働日）をこなせる重機編成を決める。
 * `one` はダンプ 1 台・ローラが現在の組数のときの能力。
 * バックホウとダンプは必要量の 80% を担える台数（四捨五入の前に 0.3 を足す）、
 * ローラは 85% を担える組数（切り上げ）にする。
 */
export function sizeFleet(
  need: number,
  p: Pick<Plan, 'qEx' | 'rollers'>,
  one: Pick<Capacity, 'truck' | 'roller'>,
): Pick<Plan, 'ex' | 'trucks' | 'rollers'> {
  return {
    ex: clamp(Math.round(need / (p.qEx * 0.8) + 0.3), 1, 8),
    trucks: clamp(Math.round(need / (one.truck * 0.8) + 0.3), 1, 40),
    rollers: Number.isFinite(one.roller)
      ? clamp(Math.ceil(need / ((one.roller / p.rollers) * 0.85)), 1, 6)
      : p.rollers,
  };
}

/** 現場の定義と標高から、現場 1 件を作る（工程・計画地盤高・重機編成・η の初期値を決める）。 */
export function createSite({ def, terrain }: SiteInput): Site {
  const kind = KINDS[def.kind];
  const region = REGIONS[def.pref];
  if (!region) throw new Error(`地域係数がない都道府県: ${def.pref}`);
  const i = def.seq - 1;
  const prefCode = Math.floor(def.lgcode / 10000);
  // 番地はダミー。町字が「丁目」で終わるかどうかで形を変える
  const banchi = /丁目$/.test(def.town)
    ? `${((i * 7) % 20) + 1}-${((i * 13) % 30) + 1}`
    : `${100 + i * 37}番地`;

  // 工程。架空の実績は「計画進捗 × 施工効率」から作る
  const st = dayNumber(def.start);
  const tasks: Task[] = kind.tasks.map((t, ti) => ({
    name: t.name,
    ts: st + t.offset,
    te: st + t.offset + t.dur,
    dur: t.dur,
    w: t.weight,
    role: t.role,
    earth: t.earth,
    method: t.method,
    pred: t.pred ?? ti - 1,
    a: 0,
  }));
  for (const t of tasks) {
    const pp = planProgress(t, B);
    const a =
      pp >= 1
        ? B - t.te > 45
          ? 1
          : Math.min(1, def.perf + ((B - t.te) / 45) * (1 - def.perf))
        : clamp(pp * def.perf, 0, 1);
    t.a = Math.round(a * 100) / 100;
    if (t.a > 0.995) t.a = 1;
  }
  const earthIdx = tasks.findIndex((t) => t.earth);
  const earth = tasks[earthIdx];
  if (!earth) throw new Error(`土工の工種がない種類: ${def.kind}`);

  const blocks = kind.blocks(def.w, def.d);
  const mb = blocks[0];
  const crane =
    kind.crane && mb?.kind === 'bldg'
      ? {
          x: mb.x,
          z: mb.z - (mb.d / 2 + 7),
          h: mb.n * mb.uh + 18,
          jib: Math.round(Math.max(mb.w, mb.d) * 1.1),
        }
      : null;

  const area = { w: def.w, d: def.d };
  const yRef = round1(terrain.emed);
  const ctx = buildContext(terrain);
  const s: Omit<Site, 'p0' | 'eta' | 'etaSrc' | 'achieved'> = {
    id: `S-${String(prefCode).padStart(2, '0')}-${String(def.seq).padStart(3, '0')}`,
    seq: def.seq,
    name: def.name,
    kindKey: def.kind,
    kind,
    type: kind.type,
    pref: def.pref,
    city: def.city,
    town: def.town,
    banchi,
    addr: `${def.pref}${def.city}${def.town}${banchi}`,
    machiaza: def.machiazaId,
    lon: def.lon,
    lat: def.lat,
    client: def.client,
    scale: kind.scale,
    dept: `${kind.type}部 ${stripPrefSuffix(def.pref)}工事事務所`,
    color: SITE_COLORS[i % SITE_COLORS.length]!,
    area,
    region,
    gate: { x: Math.round(def.w * 0.3), z: def.d / 2, ox: 0, oz: 1 },
    perf: def.perf,
    soil: def.soil,
    tasks,
    planStart: Math.min(...tasks.map((t) => t.ts)),
    planEnd: Math.max(...tasks.map((t) => t.te)),
    earthIdx,
    blocks,
    crane,
    yRef,
    FH: yRef,
    FH0: yRef,
    pit: kind.pit,
    terrainSrc: terrain.src,
    elev: { min: terrain.emin, max: terrain.emax, med: terrain.emed },
    ctx,
    g: buildGrid(terrain, ctx, area, yRef),
  };

  // 計画地盤高：建築＝現況の中央値 + 0.3m、土木＝切盛がつり合う高さ（二分法）
  const usable = 85;
  if (kind.type === '土木') {
    const so = SOILS[def.soil];
    let lo = yRef - 15;
    let hi = yRef + 15;
    for (let it = 0; it < 32; it++) {
      s.FH = (lo + hi) / 2;
      buildDesign(s);
      if (((s.g.Vc * usable) / 100) * so.C - s.g.Vf > 0) lo = s.FH;
      else hi = s.FH;
    }
    s.FH = round1(s.FH);
  } else {
    s.FH = round1(terrain.emed + 0.3);
  }
  buildDesign(s);
  s.FH0 = s.FH;

  // 施工計画の初期値：地域係数を使い、計画の土工期間で終わる編成にする
  const p: Plan = {
    soil: def.soil,
    usable,
    cap: 6.0,
    ex: 1,
    qEx: 300,
    trucks: 1,
    dExt: region.dExt,
    rollers: 1,
    qR: 600,
    hours: 8,
    days: 6,
    rain: region.rain,
    winterF: Math.round(region.wf * 100),
  };
  const cw = buildWorkCurve(region, p);
  const eb = earthBalance(s, p);
  const need = eb.Q / Math.max(5, workAt(cw, earth.te) - workAt(cw, earth.ts));
  Object.assign(p, sizeFleet(need, p, capacity({ ...p, trucks: 1 }, eb)));

  return { ...s, p0: { ...p }, ...calibrate(s, p) };
}

/** 現場の一覧を作る。ID の昇順に並べる。 */
export function createSites(inputs: readonly SiteInput[]): Site[] {
  return inputs.map(createSite).sort((a, b) => (a.id < b.id ? -1 : 1));
}

/**
 * 計画地盤高と根切り深さを変え、計画面と土量を作り直す（What-if）。
 * 現場を書きかえる唯一の操作。η は変えない。
 */
export function setGrade(s: Site, grade: { FH?: number; pit?: number }): void {
  // 数でない値は地形を NaN で壊すので、書きかえる前に止める（範囲に収めるのは画面の状態の役目）
  for (const [k, v] of Object.entries(grade)) {
    if (v !== undefined && !Number.isFinite(v))
      throw new RangeError(`${k} が数ではない: ${String(v)}`);
  }
  if (grade.pit !== undefined && grade.pit < 0) throw new RangeError(`pit は 0 以上: ${grade.pit}`);
  if (grade.FH !== undefined) s.FH = grade.FH;
  if (grade.pit !== undefined) s.pit = grade.pit;
  buildDesign(s);
}

/** 計画地盤高と根切り深さを初期値に戻す。 */
export function resetGrade(s: Site): void {
  setGrade(s, { FH: s.FH0, pit: s.kind.pit });
}
